import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { motion, AnimatePresence } from 'framer-motion';

const practiceAreas = [
  'Civil Litigation',
  'Criminal Law',
  'Writ Petitions',
  'Bail Matters',
  'Matrimonial & Family',
  'FIR & Cognizance Quashing',
  'Cheque Bounce NI Act',
  'Property & Land Disputes',
  'Consumer Protection',
  'Corporate & Commercial',
  'Banking & NBFC Disputes',
  'Arbitration & Mediation',
  'Service Matters',
  'DRT Cases',
  'NCLT / NCLAT',
  'PIL',
  'High Court & Supreme Court',
  'Legal Drafting',
  'Railway Claims'
];

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showPracticeDropdown, setShowPracticeDropdown] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        backgroundColor: scrolled ? 'rgba(255,255,255,0.95)' : 'transparent',
        backdropFilter: scrolled ? 'blur(10px)' : 'none',
        boxShadow: scrolled ? '0 2px 20px rgba(0,0,0,0.1)' : 'none',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">

          {/* Logo + Name */}
          <div className="flex-shrink-0 flex items-center space-x-2">
            <img
              src="https://customer-assets.emergentagent.com/job_5ecb04eb-657f-4ef6-b290-78fd64f7315c/artifacts/nnbtqpn5_logo.png"
              alt="NamoriSatya Legal"
              className="h-10 md:h-16 w-auto"
            />
            <div className="flex flex-col">
              <span
                className="text-sm md:text-lg font-black tracking-wide leading-tight"
                style={{
                  color: scrolled ? '#1A1A1A' : 'white',
                  textShadow: scrolled ? 'none' : '0 2px 8px rgba(0,0,0,0.8)',
                }}
              >
                NAMORISATYA LEGAL & ASSOCIATES
              </span>
              <span
                className="text-xs md:text-sm font-semibold tracking-wider"
                style={{
                  color: scrolled ? '#2b4bff' : 'white',
                  textShadow: scrolled ? 'none' : '0 2px 8px rgba(0,0,0,0.8)',
                }}
              >
                Law Firm
              </span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('about')}
              className="text-base font-medium hover:opacity-70 transition-opacity"
              style={{ color: scrolled ? '#1A1A1A' : 'white' }}
            >
              About
            </button>
            <div
              className="relative"
              onMouseEnter={() => setShowPracticeDropdown(true)}
              onMouseLeave={() => setShowPracticeDropdown(false)}
            >
              <button
                className="text-base font-medium hover:opacity-70 transition-opacity flex items-center"
                style={{ color: scrolled ? '#1A1A1A' : 'white' }}
              >
                Practice Areas
                <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              <AnimatePresence>
                {showPracticeDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border py-2 max-h-96 overflow-y-auto"
                  >
                    {practiceAreas.map((area, index) => (
                      <button
                        key={index}
                        onClick={() => scrollToSection('practice-areas')}
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors"
                        style={{ color: '#1A1A1A' }}
                      >
                        {area}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <button
              onClick={() => scrollToSection('blog')}
              className="text-base font-medium hover:opacity-70 transition-opacity"
              style={{ color: scrolled ? '#1A1A1A' : 'white' }}
            >
              Blog
            </button>
            <Button
              onClick={() => scrollToSection('contact')}
              className="text-base font-semibold px-6 transition-all duration-300 hover:opacity-90"
              style={{ backgroundColor: '#2B2BFF', color: 'white' }}
            >
              Contact Us
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-md"
              style={{ color: scrolled ? '#1A1A1A' : 'white' }}
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden"
            style={{ backgroundColor: 'rgba(255,255,255,0.95)' }}
          >
            <div className="px-4 py-4 space-y-3">
              <button
                onClick={() => scrollToSection('about')}
                className="block w-full text-left py-2 text-base font-medium"
                style={{ color: '#1A1A1A' }}
              >
                About
              </button>
              <button
                onClick={() => scrollToSection('practice-areas')}
                className="block w-full text-left py-2 text-base font-medium"
                style={{ color: '#1A1A1A' }}
              >
                Practice Areas
              </button>
              <button
                onClick={() => scrollToSection('blog')}
                className="block w-full text-left py-2 text-base font-medium"
                style={{ color: '#1A1A1A' }}
              >
                Blog
              </button>
              <Button
                onClick={() => scrollToSection('contact')}
                className="w-full text-base font-semibold"
                style={{ backgroundColor: '#2B2BFF', color: 'white' }}
              >
                Contact Us
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;